'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Fragment, useMemo, useState, type ReactElement } from 'react';
import { Prompt } from '@/types';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { hasVariables } from '@/lib/prompt-vars';
import { useToast } from '@/lib/toast';
import { copyToClipboard } from '@/lib/clipboard';
import { useAIModels } from '@/lib/ai-models-context';
import SavePopover from '@/components/prompt/SavePopover';
import PromptDetailModal from '@/components/prompt/PromptDetailModal';

interface Props {
  prompt: Prompt;
  onSaveToggle?: (id: string, saved: boolean) => void;
  showActions?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

// Map a category (our data model) to a colored dot like the design's ModelBadge.
const CATEGORY_DOT: Record<string, string> = {
  Writing:   'oklch(0.75 0.14 160)',  // mint
  Coding:    'oklch(0.72 0.14 230)',  // blue
  Art:       'oklch(0.72 0.15 285)',  // violet
  Business:  'oklch(0.78 0.14 75)',   // amber
  Education: 'oklch(0.75 0.14 40)',   // warm clay
  Other:     'oklch(0.58 0.008 85)',  // neutral
};

// Highlight {variables} and [VARIABLES] + leading KEYWORDS: inside the prompt preview.
function highlightPrompt(text: string) {
  const re = /(\{[^{}\n]{1,60}\}|\[[A-Z][A-Z0-9_]*\]|^[A-Z][A-Za-z ]+:(?=\s))/gm;
  const parts: (string | ReactElement)[] = [];
  let last = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('{') || token.startsWith('[')) {
      parts.push(<span key={idx++} className="var">{token}</span>);
    } else {
      parts.push(<span key={idx++} className="kw">{token}</span>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}

function formatStat(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  return String(n);
}

export default function PromptCard({ prompt, onSaveToggle, showActions, onEdit, onDelete }: Props) {
  const router = useRouter();
  const { user } = useAuth();
  const toast = useToast();
  const { models: aiModels } = useAIModels();
  const [isSaved, setIsSaved] = useState(prompt.is_saved);
  const [saveCount, setSaveCount] = useState(prompt.save_count);
  const [copyCount, setCopyCount] = useState(prompt.copy_count ?? 0);
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [heartPop, setHeartPop] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const isInteractive = useMemo(() => hasVariables(prompt.content), [prompt.content]);
  const aiModelById = useMemo(() => new Map(aiModels.map((m) => [m.id, m])), [aiModels]);
  const categoryKey = prompt.category || 'Other';
  const dotColor = CATEGORY_DOT[categoryKey] ?? CATEGORY_DOT.Other;
  // Prefer images[0] for display; fall back to cover_image_url (legacy)
  const displayImage = (prompt.images && prompt.images.length > 0 ? prompt.images[0] : undefined) ?? prompt.cover_image_url;
  const hasImage = !!displayImage && !imgError;
  const extraImageCount = (prompt.images?.length ?? 0) > 1 ? prompt.images!.length - 1 : 0;
  const visibleModels = (prompt.models ?? []).slice(0, 3);
  const extraModelCount = (prompt.models?.length ?? 0) > 3 ? (prompt.models!.length - 3) : 0;

  // Card click → quick-look modal. Cmd/Ctrl/middle-click still jumps to the
  // full page (keeps "open in new tab" working for shares + SEO).
  const openDetail = (e?: React.MouseEvent) => {
    if (e && (e.metaKey || e.ctrlKey || e.button === 1)) {
      router.push(`/prompt/${prompt.id}`);
      return;
    }
    setModalOpen(true);
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) { router.push('/auth/login'); return; }
    if (saving) return;
    setSaving(true);
    setHeartPop(true);
    setTimeout(() => setHeartPop(false), 400);
    try {
      if (isSaved) {
        await api.prompts.unsave(prompt.id);
        setIsSaved(false);
        setSaveCount((c) => Math.max(0, c - 1));
        onSaveToggle?.(prompt.id, false);
        toast.info('Removed from Saved');
      } else {
        await api.prompts.save(prompt.id);
        setIsSaved(true);
        setSaveCount((c) => c + 1);
        onSaveToggle?.(prompt.id, true);
        toast.success('Saved to library');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (copyBusy) return; // brief debounce — stops double-clicks double-counting
    setCopyBusy(true);
    setTimeout(() => setCopyBusy(false), 800);
    const ok = await copyToClipboard(prompt.content);
    if (!ok) {
      toast.error('Couldn\u2019t access clipboard');
      return;
    }
    toast.success('Copied to clipboard');
    // Optimistic bump; server reconciles (no-op if throttled).
    setCopyCount((c) => c + 1);
    api.prompts.copy(prompt.id)
      .then((r) => {
        if (typeof r?.copy_count === 'number') setCopyCount(r.copy_count);
      })
      .catch(() => { /* ignore — UI already updated */ });
  };

  const ModelBadge = (
    <span className="ps-model-badge">
      <span className="model-dot" style={{ background: dotColor }} />
      {categoryKey}
      {isInteractive && (
        <>
          <span style={{ opacity: 0.5, margin: '0 2px' }}>·</span>
          <span className="inline-flex items-center gap-0.5" title="Interactive prompt">
            <span className="material-symbols-outlined" style={{ fontSize: 10 }}>flash_on</span>
          </span>
        </>
      )}
    </span>
  );

  const OverlayActions = (
    <div className="ps-card-overlay-actions" onClick={(e) => e.stopPropagation()}>
      <SavePopover
        promptId={prompt.id}
        initialSaved={isSaved}
        onSavedChange={(s) => {
          setIsSaved(s);
          setSaveCount((c) => s ? c + 1 : Math.max(0, c - 1));
          onSaveToggle?.(prompt.id, s);
        }}
        variant="icon"
        align="right"
      />
      <button
        onClick={handleCopy}
        disabled={copyBusy}
        aria-busy={copyBusy || undefined}
        aria-label="Copy prompt"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
      </button>
      {showActions && onEdit && (
        <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEdit(prompt.id); }} aria-label="Edit">
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
        </button>
      )}
      {showActions && onDelete && (
        <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(prompt.id); }} aria-label="Delete"
          style={{ color: 'var(--color-error)' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
        </button>
      )}
    </div>
  );

  const Meta = (
    <div className="ps-card-meta">
      <Link
        href={`/profile/${prompt.user.username}`}
        onClick={(e) => e.stopPropagation()}
        className="ps-card-author no-underline hover:text-ink"
        title={`View ${prompt.user.name}'s profile`}
      >
        {prompt.user.avatar_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={prompt.user.avatar_url} alt={prompt.user.name} className="ps-avatar" style={{ background: 'none', objectFit: 'cover' }} />
        ) : (
          <span className="ps-avatar">{prompt.user.name.slice(0, 2).toUpperCase()}</span>
        )}
        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {prompt.user.name}
        </span>
      </Link>
      <div className="ps-card-stats">
        <span title={`${saveCount.toLocaleString()} saves`}>
          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>bookmark</span>
          {formatStat(saveCount)}
        </span>
        <span title={`${copyCount.toLocaleString()} copies`}>
          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>content_copy</span>
          {formatStat(copyCount)}
        </span>
        <span title={`${prompt.view_count.toLocaleString()} views`}>
          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>visibility</span>
          {formatStat(prompt.view_count)}
        </span>
      </div>
    </div>
  );

  return (
    <article className="ps-card" onClick={(e) => openDetail(e)}>
      {hasImage ? (
        <div className="ps-card-media">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={displayImage}
            alt={prompt.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgError(true)}
          />
          {ModelBadge}
          {OverlayActions}
          {extraImageCount > 0 && (
            <div style={{
              position: 'absolute', top: 10, right: 10, zIndex: 2,
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '3px 7px',
              background: 'color-mix(in oklch, black 55%, transparent)',
              color: 'white', fontSize: 10, fontWeight: 600,
              borderRadius: 'var(--radius-full)',
              backdropFilter: 'blur(10px)',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 11 }}>collections</span>
              +{extraImageCount}
            </div>
          )}
          {prompt.forked_from && (
            <div style={{
              position: 'absolute', bottom: 10, left: 10, zIndex: 2,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 8px',
              background: 'color-mix(in oklch, black 55%, transparent)',
              color: 'white', fontSize: 10, fontWeight: 500,
              borderRadius: 'var(--radius-full)',
              backdropFilter: 'blur(10px)',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 11 }}>fork_right</span>
              remix
            </div>
          )}
        </div>
      ) : (
        <div className="ps-card-prompt-preview">
          {highlightPrompt(prompt.content.slice(0, 260))}
        </div>
      )}
      <div className="ps-card-body">
        {!hasImage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, position: 'relative', left: 0 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 8px',
              fontSize: 11, fontWeight: 500,
              color: 'var(--ink-2)',
              background: 'var(--bg-soft)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-full)',
            }}>
              <span className="model-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor }} />
              {categoryKey}
            </span>
            {isInteractive && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: 11, color: 'var(--accent)',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>flash_on</span>
                interactive
              </span>
            )}
            {prompt.forked_from && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: 11, color: 'var(--ink-3)',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>fork_right</span>
                remix
              </span>
            )}
          </div>
        )}
        <h3 className="ps-card-title">{prompt.title}</h3>
        {prompt.description && <p className="ps-card-desc">{prompt.description}</p>}
        {/* AI model chips */}
        {visibleModels.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
            {visibleModels.map((id) => {
              const m = aiModelById.get(id);
              return (
                <span key={id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 7px',
                  fontSize: 10, fontWeight: 500,
                  borderRadius: 'var(--radius-full)',
                  background: m ? `${m.color}18` : 'var(--bg-soft)',
                  color: m?.color ?? 'var(--ink-3)',
                  border: `1px solid ${m?.color ?? 'var(--line)'}33`,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: m?.color ?? 'var(--ink-3)', flexShrink: 0 }} />
                  {m?.name ?? id}
                </span>
              );
            })}
            {extraModelCount > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '2px 7px',
                fontSize: 10, fontWeight: 500,
                borderRadius: 'var(--radius-full)',
                background: 'var(--bg-soft)',
                color: 'var(--ink-3)',
                border: '1px solid var(--line)',
              }}>
                +{extraModelCount}
              </span>
            )}
          </div>
        )}
        {Meta}
      </div>

      {/* Quick-look modal — opens on card click, dismisses on ESC / backdrop / × */}
      {modalOpen && (
        <PromptDetailModal
          prompt={prompt}
          onClose={() => setModalOpen(false)}
          onSaveToggle={(id, saved) => {
            setIsSaved(saved);
            setSaveCount((c) => Math.max(0, c + (saved ? 1 : -1)));
            onSaveToggle?.(id, saved);
          }}
        />
      )}
    </article>
  );
}
